import { useState, useMemo, useRef, useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import _omit from 'lodash/omit'
import _pick from 'lodash/pick'
import _isEmpty from 'lodash/isEmpty'
import _isEqual from 'lodash/isEqual'
import { PAGINATION_LIMIT } from 'constants/api'
import ListCard from 'app/ProviderSearch/ListCard'
import PageLoader from 'common/PageLoader'
import classNames from 'classnames'
import ErrorComponent from 'common/ErrorComponent'
import EmptySearchResult from 'app/ProviderSearch/EmptySearchResult'
import {
  fetchTimezoneFromLocation,
  ProviderFilterTypes,
} from 'dataSource/ProviderSearch'
import {
  formatPracticesFromProviders,
  isOnlyAllProviderFilter,
  getGenderValuesFromIds,
  getSlotsStartEndDate,
  matchTimezoneFromList,
  formatSupplyReserveParams,
  hasSecondaryFilters,
} from 'app/ProviderSearch/utils'
import qs from 'qs'
import {
  fetchDataAsync,
  hasError,
  getParsedQuery,
  isDateEqual,
  modifyURLQuery,
} from 'utils/common'
import useApi from 'hooks/useApi'
import { PROVIDERS, PRACTICES } from 'dataSource/ProviderSearch/routes'
import { CachePolicies } from 'use-http'
import SlotDateContext from 'context/ProviderBooking/SlotDate'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { InsuranceProviderContext } from 'context/common/InsuranceProvider'
import ProviderListPagination from '../ProviderListPaginationV3'
import ProviderSlotContext from 'context/ProviderBooking/ProviderSlots'
import { sessionStorageKeys } from 'constants/common'
import toDate from 'date-fns/toDate'
import { callRudderEventForAvailableProviderShown } from 'app/ProviderSearch/rudderEventUtils'
import useThriveAuth from 'hooks/useThriveAuth'
import DatePaginator from 'common/DatePaginator'
import useBreakpoint from 'hooks/useBreakPoint'
import { Button } from 'component_library/1_Atoms'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { getTreatment } from 'utils/treatment'
import { SLOT_LOCATION_MAP } from 'app/ProviderSearch/constants'

import styles from './index.module.scss'
import commonStyles from '../common.module.scss'

const ProvidersList: React.FC<ProvidersListProps> = ({
  groupedFilters,
  filterLookup,
  providerList,
  providerListLoading,
  onSetProviderListLoading,
  onSetProviderList,
  providerPagination,
  setProviderPagination,
  onSetPracticesList,
  selectedPractice,
  onSelectPractice,
  shouldFetchPractices = true,
  scrollContainer,
  inPersonFilterSelected,
  providerCardDetails,
  countDetails,
  isWaterfallFlow = false,
  isZeroProviderState = false,
  isDirectMatch0WithOnlyPrimaryFilter,
  waterfallProviderInsuranceInfo,
}) => {
  const router = useRouter()
  const { query } = router
  const { state, location } = query
  const { isLoading, isSupportOrThirdPartyUser, user } = useThriveAuth()
  const { businessPartner } = user ?? {}
  const [providerListError, setProviderListError] = useState<AlertMessage>(null)
  const [providerListResult, setProviderListResult] = useState<Provider[]>(null)
  const [intakeSupplyReserveData, setIntakeSupplyReserveData] = useState<
    Partial<IntakeSupplyReserveDetails>
  >({})
  const { request: apiRequest } = useApi(null, null, CachePolicies.CACHE_FIRST)
  const previousQueryRef = useRef<string>('')
  const focusProviderCardRef = useRef<HTMLDivElement>(null)

  const { timeZone, setTimeZone, setTimeZoneObject } = useContext(
    TimeZoneContext
  )
  const { startSlotDate, setStartSlotDate, slotListLimit } = useContext(
    SlotDateContext
  )
  const { insuranceProvider } = useContext(InsuranceProviderContext)
  const { providerSlots, loading: slotsLoading } = useContext(
    ProviderSlotContext
  )
  const previousSelectedSlot: SelectedSlot = useMemo(
    () =>
      JSON.parse(
        sessionStorage.getItem(sessionStorageKeys.PREVIOUS_SELECTED_SLOT)
      ),
    [providerList]
  )

  const { isExtraLargeSize, isAboveXLSize } = useBreakpoint()
  const isDesktop = isExtraLargeSize || isAboveXLSize

  // cases when we show empty screen
  const showEmptyScreen =
    (!providerList?.length && !isWaterfallFlow) ||
    (!providerList?.length && isWaterfallFlow && isZeroProviderState)

  /**
   * Fetches providers with the selected filters.
   * And based on if filters is applied or not, either fetchPractices or fetchRestProvidersForPractice
   * Will be called to get the practices.
   */
  const fetchProviders = async (parsedQuery): Promise<void> => {
    try {
      if (!hasSecondaryFilters(parsedQuery)) {
        isDirectMatch0WithOnlyPrimaryFilter.current = false
      }

      // Scrolls to the top before user provider api call
      scrollContainer?.scrollTo({
        top: 0,
      })

      if (!providerListLoading) {
        onSetProviderListLoading(true)
      }

      onSetProviderList(null)

      const {
        practiceId,
        slotStartDate,
        latitude,
        longitude,
        partnershipCode,
      } = parsedQuery

      let timezoneToUse = timeZone

      // Fetching the timezone from location for a support or third party support partner user
      if (isSupportOrThirdPartyUser) {
        const parsedPreviousQuery = qs.parse(previousQueryRef.current)

        // Only make the timezone API call when the location changes
        if (
          parsedPreviousQuery.latitude !== parsedQuery.latitude ||
          parsedPreviousQuery.longitude !== parsedQuery.longitude
        ) {
          const timezoneObjectFromLocation = await fetchTimezoneFromLocation(
            latitude,
            longitude
          )
          timezoneToUse = timezoneObjectFromLocation.timeZoneId

          const matchingTimezone = matchTimezoneFromList(
            timezoneObjectFromLocation.timeZoneName,
            timezoneObjectFromLocation.timeZoneId
          )

          setTimeZone(timezoneObjectFromLocation.timeZoneId)
          setTimeZoneObject(matchingTimezone)
        }
      }

      const utcStartTime = zonedTimeToUtc(
        toDate(parseInt(slotStartDate)),
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )

      const { startDate, endDate } = getSlotsStartEndDate(
        utcToZonedTime(utcStartTime, timezoneToUse),
        slotListLimit,
        timezoneToUse
      )

      const isSupplyReserveFlagEnabled =
        getTreatment(TreatmentFlags.SUPPLY_RESERVE, {
          state,
        }) === treatmentFlagValues.ON
      const {
        id: b2bPartnerId,
        isActive: isB2bPartnerActive,
        partnershipCode: existingB2bPartnerKeyword,
      } = businessPartner ?? {}
      const isExistingB2bUser = isB2bPartnerActive && !!b2bPartnerId
      const b2bPartnershipCode = isExistingB2bUser
        ? existingB2bPartnerKeyword
        : partnershipCode

      const queryObj = {
        ..._pick(parsedQuery, [
          'state',
          'longitude',
          'latitude',
          'offset',
          'limit',
          'areaOfConcern',
          'insuranceId',
          'counselingType',
          'availabilityType',
          'orderBy',
          'gender',
          'clientAgeGroup',
          'language',
          'counselingApproach',
          'practiceId',
          'timeOfDay',
        ]),
        slotStartDate: startDate.toISOString(),
        slotEndDate: endDate.toISOString(),
        ...(isSupplyReserveFlagEnabled &&
          !!b2bPartnershipCode && { partnershipCode: b2bPartnershipCode }),
        ...(!!parsedQuery.timeOfDay && { timezone: timezoneToUse }),
      }

      const qsQuery = qs.stringify(queryObj, {
        addQueryPrefix: true,
        skipNulls: true,
      })
      const requestUrl = `${PROVIDERS}${qsQuery}`

      // If the practiceId is removed from url then remove from state as well
      if (!practiceId) {
        onSelectPractice(null)
      }

      const responseProviderList = await apiRequest.get(requestUrl)
      const {
        result: providerListResult,
        pagination,
        intakeSupplyReserveDetails,
      } = responseProviderList
      hasError(apiRequest, responseProviderList)

      if (
        providerListResult?.length === 0 &&
        !hasSecondaryFilters(parsedQuery)
      ) {
        isDirectMatch0WithOnlyPrimaryFilter.current = true
      }

      providerListResult?.forEach((provider: ListCardProvider) => {
        const { providerSlugs } = provider.practice
        provider.practice.providerSlugs = providerSlugs.map((slug) => ({
          name: slug,
        }))
        provider.filters = provider.availabilityTypeIds?.reduce(
          (filters, id) => [...filters, filterLookup[id]?.[0]],
          []
        )
      })

      onSetProviderList(providerListResult)
      setIntakeSupplyReserveData(intakeSupplyReserveDetails)
      setProviderListResult(providerListResult)
      setProviderPagination(pagination)
    } catch (error) {
      setProviderListError({
        timestamp: new Date().getTime(),
        message: error?.message,
      })
    } finally {
      onSetProviderListLoading(false)
      const omitQueryArray = ['offset', 'partnershipCode']
      const parsedLatestQuery = _omit(parsedQuery, omitQueryArray)

      previousQueryRef.current = qs.stringify(parsedLatestQuery, {
        skipNulls: true,
      })
    }
  }

  // TODO Move the practice related logic to PracticesMap component

  /**
   * - This function makes an API call to an explicit practice API to fetch the practices rather then
   *   calling each paginated providers API and extracting the practices from it.
   *  - This practices API's response is cached from the backend and only caches the per location responses without any filtered applied.
   *  - Thats why this function is only called when there is no filtered applied.
   */
  const fetchPractices = async (queryObj: qs.ParsedQs) => {
    const formattedQuery = qs.stringify(
      _omit(queryObj, [
        'limit',
        'location',
        'slotStartDate',
        'slotEndDate',
        'partnershipCode',
      ]),
      {
        skipNulls: true,
      }
    )
    const requestUrl = `${PRACTICES}?${formattedQuery}`
    const responsePracticesList = await apiRequest.get(requestUrl)
    hasError(apiRequest, responsePracticesList)
    onSetPracticesList(responsePracticesList)
  }

  /**
   * - This function makes the rest of the paginated provider API calls to get all the providers
   *    then extract the practices from all these providers
   *    this api calls are being made in the background, meaning no loaders are shown for these providers
   *  - This function is called when ever a filter is applied.
   *  - When no filters is applied then fetchPractices function is called
   */
  const fetchRestProvidersForPractice = async (
    { providerListResult, pagination },
    parsedQuery
  ) => {
    const { practiceId } = parsedQuery
    const omitQueryArray = ['offset', 'partnershipCode']

    const parsedLatestQuery = _omit(parsedQuery, omitQueryArray)
    // Although this is omitted in the ref we are just double making sure
    const parsedPreviousQuery = _omit(
      qs.parse(previousQueryRef.current),
      omitQueryArray
    )

    if (!_isEqual(parsedLatestQuery, parsedPreviousQuery)) {
      let practices = formatPracticesFromProviders(providerListResult)
      onSetPracticesList(practices)

      const providerDataPromises = fetchDataAsync(
        parsedQuery,
        PROVIDERS,
        apiRequest,
        pagination
      )

      for (const providerDataPromise of providerDataPromises) {
        const providerData = await providerDataPromise
        const { result: providers = [] } = providerData ?? {}
        practices = formatPracticesFromProviders(providers, practices)
        onSetPracticesList([...practices])
      }

      // If practiceId is present and there is no selected practice state then this would
      // find the practice from the list of practices and set it
      // this case would come only when a user has a selected practice and refreshes the page
      if (practiceId && _isEmpty(selectedPractice)) {
        const practice = practices.find(
          (practice) => practice.id === practiceId
        )
        onSelectPractice(practice)
      }
    }
  }

  const fetchAllPracticesForMap = async (parsedQuery): Promise<void> => {
    try {
      if (!shouldFetchPractices) {
        return
      }

      const { slotStartDate, partnershipCode } = parsedQuery
      const utcStartTime = zonedTimeToUtc(
        toDate(parseInt(slotStartDate)),
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )

      const { startDate, endDate } = getSlotsStartEndDate(
        utcToZonedTime(utcStartTime, timeZone),
        slotListLimit,
        timeZone
      )
      const isSupplyReserveFlagEnabled =
        getTreatment(TreatmentFlags.SUPPLY_RESERVE, {
          state,
        }) === treatmentFlagValues.ON
      const {
        id: b2bPartnerId,
        isActive: isB2bPartnerActive,
        partnershipCode: existingB2bPartnerKeyword,
      } = businessPartner ?? {}
      const isExistingB2bUser = isB2bPartnerActive && !!b2bPartnerId
      const b2bPartnershipCode = isExistingB2bUser
        ? existingB2bPartnerKeyword
        : partnershipCode

      const queryObj = {
        ..._pick(parsedQuery, [
          'state',
          'longitude',
          'latitude',
          'offset',
          'limit',
          'areaOfConcern',
          'insuranceId',
          'counselingType',
          'availabilityType',
          'orderBy',
          'gender',
          'clientAgeGroup',
          'language',
          'counselingApproach',
          'practiceId',
        ]),
        slotStartDate: startDate.toISOString(),
        slotEndDate: endDate.toISOString(),
        ...(isSupplyReserveFlagEnabled &&
          !!b2bPartnershipCode && { partnershipCode: b2bPartnershipCode }),
      }
      const isAllProviderFilter = isOnlyAllProviderFilter(queryObj)

      if (isAllProviderFilter) {
        //To render Maps first in case practices are fetched separately as it is the largest Content
        fetchPractices(queryObj)
      } else {
        //When filters other than All providers are applied, practices are extracted from Provider Details
        if (_isEmpty(providerListResult)) {
          onSetPracticesList([])
          return
        }

        fetchRestProvidersForPractice(
          { providerListResult, pagination: providerPagination },
          queryObj
        )
      }
    } catch (error) {}
  }

  const onRemovePractice = () => {
    modifyURLQuery(router, {
      practiceId: undefined,
    })
  }

  const getPracticeLocationText = () => {
    let practiceLocationText = 'near '

    if (!!selectedPractice) {
      practiceLocationText = `for the ${selectedPractice.name} only.`
    }

    return practiceLocationText
  }

  const getProviderSearchQuery = () => {
    const qsQuery = getParsedQuery(router)
    const {
      offset = '0',
      limit = PAGINATION_LIMIT.toString(),
      slotStartDate = new Date().getTime().toString(),
      latitude,
      longitude,
      state,
      gender,
      ...restQuery
    } = qsQuery

    const genderValues = getGenderValuesFromIds(
      gender as string[],
      groupedFilters[ProviderFilterTypes.GENDER]
    )

    return {
      offset,
      limit,
      slotStartDate,
      latitude,
      longitude,
      state,
      ...(genderValues && { gender: genderValues }),
      ...restQuery,
    }
  }

  useEffect(() => {
    // This useEffect is created to sync the context date state with the date that is in url query

    if (startSlotDate) {
      const { slotStartDate } = getParsedQuery(router)

      if (slotStartDate) {
        const queryStartDate = toDate(parseInt(slotStartDate as string))

        if (!isDateEqual(queryStartDate, startSlotDate)) {
          setStartSlotDate(queryStartDate)
        }
      }
    }
  }, [router.asPath, startSlotDate])

  useEffect(() => {
    // Used as asPath (i.e. router.asPath) instead of router query because
    // On initial load of the page when there is a query param in the url
    // the use effect is called twice while using router.query
    // first without the params and second with the params
    // so the api is called twice with 2 different params
    // asPath only updates once on page load and on url change
    const disableProviderFetch = sessionStorage.getItem(
      sessionStorageKeys.DISABLE_PROVIDER_FETCH_ONCE
    )
    const providerSearchQuery = getProviderSearchQuery()
    const { latitude, longitude, state } = providerSearchQuery

    if (
      !isLoading &&
      !disableProviderFetch &&
      !!state &&
      !!latitude &&
      !!longitude
    ) {
      fetchProviders(providerSearchQuery)
    }

    sessionStorage.removeItem(sessionStorageKeys.DISABLE_PROVIDER_FETCH_ONCE)
  }, [router.asPath, isLoading])

  useEffect(() => {
    if (shouldFetchPractices && !_isEmpty(providerListResult)) {
      const providerSearchQuery = getProviderSearchQuery()
      fetchAllPracticesForMap(providerSearchQuery)
    }
  }, [providerListResult, shouldFetchPractices])

  /*=====================This useEffect contains scroll logic===============================*/
  useEffect(() => {
    if (previousSelectedSlot?.slot && focusProviderCardRef?.current) {
      scrollContainer?.scrollTo({
        behavior: 'smooth',
        top: focusProviderCardRef.current.offsetTop,
      })
      sessionStorage.setItem(sessionStorageKeys.PREVIOUS_SELECTED_SLOT, null)
    }
  }, [providerSlots, previousSelectedSlot])

  useEffect(() => {
    if (providerListResult && !slotsLoading) {
      // This useEffect has been added to satisfy the following requirements for triggering this event
      // 1. The slots must be loaded along with all the providers list
      // 2. If the API for providers fails the providerListResult still remains null which will not trigger this event,
      //providerSlots in this case remains [] which would falsely trigger this event
      //Pass intake supply params to the available providers shown event only when supply reserve flag is on
      const isSupplyReserveFlagEnabled =
        getTreatment(TreatmentFlags.SUPPLY_RESERVE, {
          state,
        }) === treatmentFlagValues.ON
      const supplyReserveParams = isSupplyReserveFlagEnabled
        ? formatSupplyReserveParams(intakeSupplyReserveData, user?.userType)
        : {}

      // In desktop view, the user is shown 5 days worth of slots for each provider.
      // But in mobile view, the user is only shown the next available slot for each provider.
      // So, in mobile view, set slot_sum to next_available_slot_sum so that Rudderstack tracking is accurate.
      if (!isDesktop) {
        countDetails.slot_sum = countDetails.next_available_slot_sum
      }

      if (!providerListLoading && !isWaterfallFlow) {
        callRudderEventForAvailableProviderShown({
          providers: providerListResult,
          countDetails,
          totalProvidersAvailable: providerPagination?.count,
          groupedFilters,
          query,
          startSlotDate,
          selectedPractice,
          user,
          intakeSupplyReserveParams: supplyReserveParams,
          timeZone,
        })
      }

      if (
        !providerListLoading &&
        isWaterfallFlow &&
        !hasSecondaryFilters(getParsedQuery(router))
      ) {
        callRudderEventForAvailableProviderShown({
          providers: providerListResult,
          countDetails,
          totalProvidersAvailable: providerPagination?.count,
          groupedFilters,
          query,
          startSlotDate,
          selectedPractice,
          user,
          intakeSupplyReserveParams: supplyReserveParams,

          directProvidersCount: providerListResult?.length,
          indirectProvidersCount: 0,
          directSlotsCountDetails: countDetails,
          indirectSlotsCountDetails: { slot_sum: 0, provider_count: [] },
          timeZone,
        })
      }
    }
  }, [providerListResult, slotsLoading, isWaterfallFlow])

  const renderInNetworkSection = () => {
    return (
      <div className="~flex ~items-center">
        <img
          src="/icons/shield-check2.svg"
          srcSet="/icons/shield-check2.webp"
          alt="insurance in-network"
          className="~m-[6px] ~h-5 ~w-5"
        />
        <p className="~font-medium ~text-sm ~p-[5px]">
          In-network with {insuranceProvider?.name}
        </p>
      </div>
    )
  }

  const isWaterfallFlowWithNoProviders =
    isWaterfallFlow && !providerList?.length && !isZeroProviderState

  const {
    noWaterfallProviderAcceptsInsurance,
    notInNWProvidersCount,
  } = waterfallProviderInsuranceInfo

  return (
    <>
      <ErrorComponent error={providerListError} />
      <PageLoader
        isLoading={providerListLoading}
        isEmpty={showEmptyScreen}
        loadingMessage="Finding Amazing Providers"
        loadingContainerClass={styles.provider_list_loader_container}
        Components={{
          Empty: (
            <EmptySearchResult containerClassName="available-page-height flex-center" />
          ),
        }}
      >
        {isWaterfallFlowWithNoProviders && noWaterfallProviderAcceptsInsurance && (
          <div className="~flex ~items-center ~justify-center ~mt-10 ~mb-7 lg:~mt-14 lg:~mb-14">
            <div className="~flex ~items-center ~justify-center ~flex-col ~gap-6 lg:~gap-10 ~w-[400px]">
              <img
                src="/icons/waterfall-no-in-nw-providers.svg"
                alt="no in-network providers"
                className="~h-[177px] ~w-[295px]"
              />
              <p className="~text-base lg:~text-lg ~font-medium ~text-center ~font-gibson">
                Currently, there are no in-network providers available, but{' '}
                {notInNWProvidersCount} providers below accept self-pay.
              </p>
            </div>
          </div>
        )}

        <div className={styles.new_date_paginator_container}>
          {!isWaterfallFlow && (
            <div className={styles.providers_found_container}>
              {providerPagination.count >= 1 && (
                <span className={styles.providers_found_text}>
                  {`${providerPagination.count} provider${
                    providerPagination.count > 1 ? 's' : ''
                  } found`}
                  &nbsp;
                </span>
              )}
              <span
                className={classNames(
                  {
                    'mr-2': !!selectedPractice,
                  },
                  styles.practice_location_btn
                )}
              >
                {getPracticeLocationText()}
                {!selectedPractice && location}
              </span>
              {selectedPractice && (
                <Button
                  variant="secondary"
                  className={styles.clear_practice_btn}
                  onClick={onRemovePractice}
                >
                  Clear
                </Button>
              )}
            </div>
          )}

          {isDesktop && (
            <DatePaginator
              selectedDate={startSlotDate}
              onDateChange={(date) => {
                modifyURLQuery(router, {
                  slotStartDate: date.getTime().toString(),
                })
              }}
              className="ml-auto"
              weekDayContainerClass={styles.date_paginator_week_day_container}
              lowerDateFormat="MONTH_DATE_FORMAT"
            />
          )}
        </div>
        {isWaterfallFlowWithNoProviders &&
          !noWaterfallProviderAcceptsInsurance && ( // Empty state for no insurance is handled above the Date paginator
            <section
              className={classNames(styles.empty_container, {
                [styles.empty_container__mobile]: !isDesktop,
              })}
            >
              <header className={styles.empty_container_header}>
                Sorry, we couldn&apos;t find any providers that match all of
                your filter criteria.
              </header>
              <p className={styles.empty_container_body}>
                We found providers that match some or most of your filters
                below.
              </p>
            </section>
          )}
        {/* This div is intentional to handle gradient */}
        <div>
          {providerList?.length > 0 && isWaterfallFlow && (
            <section
              className={classNames(styles.waterfall_text_container, {
                [styles.waterfall_text_container__mobile]: !isDesktop,
                [styles.waterfall_text_container__gradient]:
                  isDesktop && isWaterfallFlow,
              })}
            >
              <span className={styles.waterfall_text_bold}>
                {`${providerPagination.count} provider${
                  providerPagination.count > 1 ? 's' : ''
                }`}
                &nbsp;
              </span>
              <span>{`in ${!selectedPractice && location} ${
                providerPagination.count > 1 ? 'are' : 'is'
              } a `}</span>
              <span className={styles.waterfall_text_bold}>100% match</span>
            </section>
          )}

          {/* Scrollable section */}
          {providerCardDetails?.map((providerCardDetails) => {
            const { providerDetails } = providerCardDetails
            return (
              <div
                ref={
                  providerDetails.id === previousSelectedSlot?.provider?.id
                    ? focusProviderCardRef
                    : null
                }
                key={`${providerDetails.id}-list-card`}
                className={styles.ProviderCard_container}
              >
                <ListCard
                  groupedFilters={groupedFilters}
                  showDistance={
                    inPersonFilterSelected || isSupportOrThirdPartyUser
                  }
                  providerCardDetails={providerCardDetails}
                  slotLocation={SLOT_LOCATION_MAP.direct_match}
                  renderCustomInsuranceSection={
                    //Since these are 100% matches for waterfall flow, we can show the in-network insurance section if an insurance is selected
                    isWaterfallFlow &&
                    insuranceProvider?.id &&
                    renderInNetworkSection
                  }
                />
              </div>
            )
          })}
          {isWaterfallFlow && hasSecondaryFilters(getParsedQuery(router)) && (
            <div
              className={classNames(
                commonStyles.waterfall_bucket_divider_container,
                {
                  [commonStyles.waterfall_bucket_divider_container__mobile]: !isDesktop,
                }
              )}
            >
              <img
                src={
                  isDesktop
                    ? '/images/waterfall-bucket-divider.svg'
                    : '/images/squiggle.svg'
                }
                alt="Waterfall bucket divider"
                className={commonStyles.waterfall_bucket_divider}
              />
            </div>
          )}
        </div>
        <ProviderListPagination providerPagination={providerPagination} />
      </PageLoader>
    </>
  )
}

export default ProvidersList
